import React, { useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import PageHeader from "../../../components/layout/PageHeader";
import Dialog from "../../../components/ui/Dialog";
import PickerChips from "../../../components/ui/PickerChips";
import { useCompanies } from "../../../api/queries/organizationQueries";
import {
  useCopyHolidayYear,
  useDeleteHoliday,
  useHolidays,
  useSaveHoliday,
} from "../../../api/queries/widgetQueries";
import { HOLIDAY_TYPE } from "../../../components/dashboard/widgetFormat";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import useBranchTargets, {
  BRANCH_SCOPE_HINT,
} from "../../../hooks/useBranchTargets";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatPlainDate, todayInManila } from "../../../utils/dateTime";
import {
  HOLIDAY_TYPES,
  NAME_MAX,
  NATIONAL,
  REMARKS_MAX,
  SKIP_REASON,
  appliesTo,
  emptyForm,
  formProblems,
  fromRow,
  toPayload,
} from "./holidayFormat";
import "../admin.css";

const EMPTY = [];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};
const ErrorList = ({ errors }) =>
  errors.length > 0 && (
    <div className="alert alert-danger fs-085 py-2">
      <ul className="mb-0 ps-3">
        {errors.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </div>
  );
const DialogButtons = ({ busy, onCancel, onSave, label }) => (
  <>
    <button
      type="button"
      className="btn btn-outline-secondary profile-btn"
      onClick={onCancel}
      disabled={busy}
    >
      Cancel
    </button>
    <button
      type="button"
      className="btn btn-primary profile-btn"
      onClick={onSave}
      disabled={busy}
    >
      {busy && (
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
      )}
      {label}
    </button>
  </>
);

const HolidayDialog = ({ row, year, onClose }) => {
  const user = useCurrentUser();
  const { superAdmin } = usePermissions();
  const { data: companies = EMPTY } = useCompanies();
  const [form, setForm] = useState(() =>
    row ? fromRow(row) : { ...emptyForm(), date: "" },
  );
  const [errors, setErrors] = useState([]);
  const save = useSaveHoliday();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const national = form.companyId === NATIONAL;
  // BRANCH scope: only managed branches, and a branch is required
  const { branchOnly, options } = useBranchTargets(
    "holidays",
    national ? null : form.companyId || user.companyId,
  );

  const submit = () => {
    const problems = formProblems(form);
    if (branchOnly && !form.branchIds.length)
      problems.push("Pick the branches it is for.");
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { id: row?.id, body: toPayload(form) },
      {
        onSuccess: () => {
          toast.success(row ? "Holiday saved." : "Holiday added.");
          onClose();
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the holiday.")),
      },
    );
  };

  return (
    <Dialog
      title={row ? "Edit holiday" : "Add holiday"}
      subtitle={!row && year ? `For ${year} (or any date)` : undefined}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <DialogButtons
          busy={save.isPending}
          onCancel={onClose}
          onSave={submit}
          label={row ? "Save" : "Add"}
        />
      }
    >
      <ErrorList errors={errors} />
      <div className="row g-3">
        <div className="col-md-5">
          <label htmlFor="hol-date" className="profile-label">
            Date <span className="text-danger">*</span>
          </label>
          <input
            id="hol-date"
            type="date"
            className="form-control"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
        </div>
        <div className="col-md-7">
          <label htmlFor="hol-name" className="profile-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            id="hol-name"
            className="form-control"
            maxLength={NAME_MAX}
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor="hol-type" className="profile-label">
            Type
          </label>
          <select
            id="hol-type"
            className="form-select"
            value={form.type}
            onChange={(e) => set({ type: e.target.value })}
          >
            {HOLIDAY_TYPES.map((t) => (
              <option key={t} value={t}>
                {HOLIDAY_TYPE[t]?.label || t}
              </option>
            ))}
          </select>
        </div>
        {!branchOnly && (
          <div className="col-md-6">
            <label htmlFor="hol-company" className="profile-label">
              Applies to
            </label>
            <select
              id="hol-company"
              className="form-select"
              value={form.companyId}
              onChange={(e) =>
                set({ companyId: e.target.value, branchIds: [] })
              }
            >
              {superAdmin && (
                <option value={NATIONAL}>Every company (national)</option>
              )}
              <option value="">
                {user.companyId ? "My company" : "Choose a company…"}
              </option>
              {companies
                .filter((c) => c.id !== user.companyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName || c.name}
                  </option>
                ))}
            </select>
          </div>
        )}
        {!national && (
          <div className="col-12">
            <label htmlFor="hol-branches" className="profile-label">
              Only these branches{" "}
              {branchOnly ? (
                <span className="text-danger">*</span>
              ) : (
                <span className="text-slate-400 fw-normal">
                  (optional, for local holidays)
                </span>
              )}
            </label>
            <PickerChips
              id="hol-branches"
              options={options}
              value={form.branchIds}
              onChange={(ids) => set({ branchIds: ids })}
              placeholder="Add a branch…"
              emptyText={
                branchOnly ? "Pick at least one branch" : "The whole company"
              }
            />
          </div>
        )}
        <div className="col-12">
          <label htmlFor="hol-remarks" className="profile-label">
            Remarks{" "}
            <span className="text-slate-400 fw-normal">
              (e.g. the proclamation number)
            </span>
          </label>
          <input
            id="hol-remarks"
            className="form-control"
            maxLength={REMARKS_MAX}
            value={form.remarks}
            onChange={(e) => set({ remarks: e.target.value })}
          />
        </div>
        <div className="col-12">
          <div className="form-check">
            <input
              id="hol-draft"
              type="checkbox"
              className="form-check-input"
              checked={form.isDraft}
              onChange={(e) => set({ isDraft: e.target.checked })}
            />
            <label htmlFor="hol-draft" className="form-check-label fs-085">
              Draft (not shown to employees yet)
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

const CopyYearDialog = ({ year, onClose }) => {
  const [fromYear, setFromYear] = useState(year - 1);
  const [toYear, setToYear] = useState(year);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const copy = useCopyHolidayYear();
  const submit = () => {
    if (Number(fromYear) === Number(toYear))
      return setErrors(["Pick two different years."]);
    setErrors([]);
    copy.mutate(
      { fromYear: Number(fromYear), toYear: Number(toYear) },
      {
        onSuccess: (res) => setResult(res),
        onError: (err) => setErrors(messagesOf(err, "Could not copy.")),
      },
    );
  };
  return (
    <Dialog
      title="Copy holidays to another year"
      subtitle="Fixed-date holidays are copied as drafts. Check them, then publish."
      onClose={onClose}
      busy={copy.isPending}
      footer={
        result ? (
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={onClose}
          >
            Done
          </button>
        ) : (
          <DialogButtons
            busy={copy.isPending}
            onCancel={onClose}
            onSave={submit}
            label="Copy"
          />
        )
      }
    >
      <ErrorList errors={errors} />
      {result ? (
        <>
          <p className="fs-085">
            <strong>{result.created}</strong> copied as drafts,{" "}
            <strong>{result.skipped}</strong> skipped.
          </p>
          {(result.skippedItems || EMPTY).length > 0 && (
            <ul className="fs-080 mb-0 ps-3">
              {result.skippedItems.map((s) => (
                <li key={`${s.date}-${s.name}`}>
                  {formatPlainDate(s.date, "MMM D")} {s.name}:{" "}
                  <span className="text-slate-500">
                    {SKIP_REASON[s.reason] || s.reason}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="row g-3">
          <div className="col-6">
            <label htmlFor="copy-from" className="profile-label">
              From
            </label>
            <input
              id="copy-from"
              type="number"
              className="form-control"
              value={fromYear}
              onChange={(e) => setFromYear(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label htmlFor="copy-to" className="profile-label">
              To
            </label>
            <input
              id="copy-to"
              type="number"
              className="form-control"
              value={toYear}
              onChange={(e) => setToYear(e.target.value)}
            />
          </div>
          <p className="fs-075 text-slate-500 mb-0">
            Holidays whose date changes every year (Holy Week, Eid, National
            Heroes Day…) are skipped: add those yourself.
          </p>
        </div>
      )}
    </Dialog>
  );
};

/** Administration → Holidays: the year's holidays (national + company), drafts and copy-year. */
const HolidaysPage = () => {
  const { noneManaged } = useBranchTargets("holidays");
  const { can, superAdmin } = usePermissions();
  const canModify = can("holidays", "modify");
  const thisYear = Number(todayInManila().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const query = useHolidays({ year, page: 1, limit: 200 });
  const rows = query.data?.data || EMPTY;
  const save = useSaveHoliday();
  const remove = useDeleteHoliday();
  const [editing, setEditing] = useState(null); // { row? }
  const [copying, setCopying] = useState(false);
  const drafts = rows.filter((r) => r.isDraft).length;
  const canEditRow = (r) => canModify && (r.companyId !== null || superAdmin);

  const publish = (r) =>
    save.mutate(
      { id: r.id, body: { isDraft: false } },
      {
        onSuccess: () => toast.success(`${r.name} published.`),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not publish it.")),
      },
    );
  const doDelete = (r) => {
    if (!window.confirm(`Remove ${r.name} (${formatPlainDate(r.date)})?`))
      return;
    remove.mutate(r.id, {
      onSuccess: () => toast.success("Holiday removed."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not remove it.")),
    });
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <PageHeader
          back={{ to: "/admin", label: "Administration" }}
          title="Holidays"
          subtitle="National and company holidays. Published ones show on the dashboards."
          actions={
            canModify && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-light border h-35px"
                  onClick={() => setCopying(true)}
                >
                  <i className="bi bi-copy me-1"></i>Copy a year
                </button>
                <span title={noneManaged ? BRANCH_SCOPE_HINT : undefined}>
                  <button
                    type="button"
                    className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                    onClick={() => setEditing({})}
                    disabled={noneManaged}
                  >
                    <i className="bi bi-plus-lg me-2"></i>Add holiday
                  </button>
                </span>
              </>
            )
          }
        />

        {noneManaged && (
          <div className="alert alert-warning fs-085 py-2">
            <i className="bi bi-exclamation-triangle me-1"></i>
            {BRANCH_SCOPE_HINT}
          </div>
        )}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <label htmlFor="hol-year" className="fs-080 text-slate-600">
            Year
          </label>
          <select
            id="hol-year"
            className="status-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[thisYear + 1, thisYear, thisYear - 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {drafts > 0 && (
            <span className="status-pill status-pill--amber">
              {drafts} draft{drafts === 1 ? "" : "s"} to check and publish
            </span>
          )}
        </div>

        {query.isError && (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(query.error, "Could not load the holidays.")}
          </div>
        )}
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0 admin-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Date</th>
                  <th className="py-3">Holiday</th>
                  <th className="py-3">Type</th>
                  <th className="py-3">Applies to</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((r) => {
                  const type = HOLIDAY_TYPE[r.type] || {
                    label: r.type,
                    tone: "gray",
                  };
                  return (
                    <tr key={r.id} className={r.isDraft ? "opacity-75" : ""}>
                      <td className="ps-4 text-nowrap">
                        {formatPlainDate(r.date, "ddd, MMM D")}
                      </td>
                      <td>
                        <span className="fw-semibold">{r.name}</span>
                        {r.remarks && (
                          <div className="fs-075 text-slate-500">
                            {r.remarks}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`status-pill status-pill--${type.tone}`}
                        >
                          {type.label}
                        </span>
                      </td>
                      <td>{appliesTo(r)}</td>
                      <td>
                        {r.isDraft ? (
                          <span className="status-pill status-pill--amber">
                            Draft
                          </span>
                        ) : (
                          <span className="status-pill status-pill--green">
                            Published
                          </span>
                        )}
                      </td>
                      <td className="pe-4 text-end text-nowrap">
                        {canEditRow(r) ? (
                          <div className="d-inline-flex gap-1">
                            {r.isDraft && (
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => publish(r)}
                                disabled={save.isPending}
                              >
                                Publish
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-light border"
                              onClick={() => setEditing({ row: r })}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light border text-danger"
                              onClick={() => doDelete(r)}
                              aria-label={`Remove ${r.name}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        ) : (
                          r.companyId === null &&
                          canModify && (
                            <span
                              className="fs-070 text-slate-400"
                              title="National holidays are set by the super admin"
                            >
                              National
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!query.isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="approval-empty">
                      No holidays for {year} yet.
                      {canModify && " Add one, or copy last year's."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {editing && (
        <HolidayDialog
          row={editing.row}
          year={year}
          onClose={() => setEditing(null)}
        />
      )}
      {copying && (
        <CopyYearDialog year={year} onClose={() => setCopying(false)} />
      )}
    </Layout>
  );
};

export default HolidaysPage;
